/**
 * Auto-pay: the saved payment method to charge, the wording being authorized,
 * and a box and typed name to turn it on. With no saved method the family adds
 * one first (Stripe sends them back to this page); when the payment processor
 * can't be reached, it says so instead. Answers save as the guardian
 * goes (see useStepAnswers). A finished step says which method it is on with.
 */
import { Checkbox } from '~/components/ui/checkbox';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { RadioGroup } from '~/components/ui/radio-group';
import { AddPaymentMethodButton } from '~/pages/MyInvoices/AddPaymentMethodButton';
import type { AutoPayContext } from '../useChecklistStep';
import { Choice, Field, Section, StepFooter, StepHeader, type StepProps } from './stepParts';
import { useFamilyEnrollmentForYear } from './useFamilyEnrollmentForYear';
import { useStepAnswers } from './useStepAnswers';

interface AutoPayAnswers {
  paymentMethodId: string;
  accepted: boolean;
  typedName: string;
}

type Method = AutoPayContext['paymentMethods'][number];

/** "Visa ••4242" or "Bank account ••6789". */
function describeMethod(method: Pick<Method, 'brand' | 'last4' | 'type'>): string {
  const label = method.type === 'us_bank_account' ? 'Bank account' : method.type === 'link' ? 'Link' : method.brand ?? 'Card';
  const name = label.charAt(0).toUpperCase() + label.slice(1);
  return method.last4 ? `${name} ••${method.last4}` : name;
}

function readAnswers(answers: unknown): AutoPayAnswers {
  const given = answers && typeof answers === 'object' && !Array.isArray(answers) ? (answers as Record<string, unknown>) : {};
  return {
    paymentMethodId: typeof given.paymentMethodId === 'string' ? given.paymentMethodId : '',
    accepted: given.accepted === true,
    typedName: typeof given.typedName === 'string' ? given.typedName : '',
  };
}

export default function AutoPayStep({ step, readOnly, overviewPath, stepPath }: StepProps) {
  const autoPay = step.autoPay;
  if (!autoPay) return null;

  return (
    <div className="space-y-6">
      {/* The form shows the intro with the wording. */}
      <StepHeader step={!readOnly && autoPay.intro ? { ...step, intro: null } : step} />
      {readOnly ? (
        autoPay.activeMethod ? (
          <Section>
            <p className="break-words">Auto-pay is on with {describeMethod(autoPay.activeMethod)}</p>
          </Section>
        ) : null
      ) : (
        <AutoPayForm step={step} autoPay={autoPay} overviewPath={overviewPath} stepPath={stepPath} />
      )}
    </div>
  );
}

function AutoPayForm({ step, autoPay, overviewPath, stepPath }: {
  step: StepProps['step'];
  autoPay: AutoPayContext;
  overviewPath: string;
  stepPath: (itemId: string) => string;
}) {
  const itemId = step.item.id;
  const { familyEnrollmentId } = useFamilyEnrollmentForYear();
  const { answers, change, submit, saveState, errors, submitting } = useStepAnswers<AutoPayAnswers>({
    itemId,
    initial: readAnswers(step.answers),
    overviewPath,
    stepPath,
  });
  // A method the family no longer has is not a choice.
  const chosen = autoPay.paymentMethods.some((method) => method.paymentMethodId === answers.paymentMethodId) ? answers.paymentMethodId : '';
  const returnUrl = `${window.location.origin}${stepPath(itemId)}`;

  return (
    <>
      <Section>
        {autoPay.intro ? <p className="whitespace-pre-line break-words">{autoPay.intro}</p> : null}
        <p className="whitespace-pre-line break-words">{autoPay.wording}</p>
      </Section>

      <fieldset disabled={submitting} className="min-w-0 space-y-6">
        <Section>
          {autoPay.paymentMethodsError ? (
            // The saved methods couldn't be asked for: not "none saved".
            <p role="alert" className="text-sm text-destructive">{autoPay.paymentMethodsError}</p>
          ) : autoPay.paymentMethods.length > 0 ? (
            <RadioGroup
              aria-label="Payment method"
              value={chosen}
              onValueChange={(paymentMethodId) => change((current) => ({ ...current, paymentMethodId }))}
              className="grid gap-2"
            >
              {autoPay.paymentMethods.map((method) => (
                <Choice key={method.paymentMethodId} id={`${itemId}-method-${method.paymentMethodId}`} value={method.paymentMethodId} label={describeMethod(method)} />
              ))}
            </RadioGroup>
          ) : null}
          {familyEnrollmentId && !autoPay.paymentMethodsError ? (
            <AddPaymentMethodButton
              familyEnrollmentId={familyEnrollmentId}
              returnUrl={returnUrl}
              variant={autoPay.paymentMethods.length > 0 ? 'outline' : 'default'}
              className="[&_button]:min-h-11 [&_button]:w-full md:[&_button]:w-auto"
            />
          ) : null}
        </Section>

        <Section>
          <div className="flex min-h-11 items-start gap-3">
            <Checkbox
              id={`${itemId}-accept`}
              className="mt-0.5 h-5 w-5"
              checked={answers.accepted}
              onCheckedChange={(value) => change((current) => ({ ...current, accepted: value === true }))}
            />
            <Label htmlFor={`${itemId}-accept`} className="text-base font-normal leading-snug">I authorize automatic payments</Label>
          </div>
          <Field id={`${itemId}-name`} label="Type your full name">
            <Input
              id={`${itemId}-name`}
              autoComplete="name"
              className="h-11 md:h-10"
              value={answers.typedName}
              onChange={(event) => change((current) => ({ ...current, typedName: event.target.value }))}
            />
          </Field>
        </Section>
      </fieldset>

      <StepFooter
        errors={errors}
        saveState={saveState}
        submitting={submitting}
        label="Turn on auto-pay"
        onSubmit={submit}
        disabled={!chosen}
      />
    </>
  );
}
